/**
 * Is there a session? (MP-MIG-001)
 *
 * Before the cutover this question was `localStorage.getItem('access_token')`
 * — the page could see its own credential. Under the BFF it cannot: the
 * cookie is HttpOnly and the tokens live server-side, which is the whole
 * point of the pattern.
 *
 * So "am I signed in" stops being a local read and becomes a question for the
 * server. Callers that only need a hint use `maybeSignedIn()`, which is
 * optimistic and cheap; anything that must be right awaits `checkSession()`
 * and treats a 401 as signed out.
 */

let cached = null;

/**
 * Optimistic, synchronous. True unless we have already been told otherwise.
 * Use it to decide whether to *attempt* a call, never to grant access — the
 * server decides that, and it is the only one that can.
 */
export const maybeSignedIn = () => cached !== false;

/**
 * Ask the server. Returns the user, or null.
 */
export const checkSession = async (api) => {
  try {
    const response = await api.get('/core/me');
    cached = true;
    return response.data?.user ?? response.data ?? null;
  } catch (error) {
    if (error?.response?.status === 401) {
      cached = false;
      return null;
    }
    // A network blip is not a signed-out user; say nothing rather than
    // bouncing somebody to a login page because the wifi dropped.
    return null;
  }
};

/** Start a login. The BFF handles the rest. */
export const startLogin = (next) => {
  const target = next || window.location.pathname + window.location.search;
  window.location.href = `/auth/login?next=${encodeURIComponent(target)}`;
};

/** End one. Clears the server session and the IdP's. */
export const startLogout = () => {
  window.location.href = '/auth/logout';
};
